import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Spinner } from "../../Components/Common/LoadingStates";
import {
  createRewardsDropCode,
  createRewardsDropCodesBulk,
  getRewardsDropCodes,
  createRewardsWhitelistDrop,
  createRewardsSnapshotDrop,
  getRewardsDrops,
} from "../../helpers/fakebackend_helper";
import {
  card, btn, btnGhost, input, td, label, COLORS, busyBtn, AppPlacement,
  shortAddr, fmtNum, fmtDate, Badge, Muted, ReadOnlyNote, Pagination, Table, pageMetaOf, readFileText,
} from "./shared";
import { apiError, listOf, normaliseCodes, parseWalletList, parseBalances, splitSnapshot, toCsv, downloadCsv } from "./rewardsUtil";

const grid = { display: "grid", gap: 10, maxWidth: 520 };
const twoCol = { display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" };
const resultBox = { marginTop: 14, padding: 12, border: "1px dashed var(--bl-border, #2a2f3a)", borderRadius: 8 };

function positiveInt(v) {
  const n = Number(v);
  return Number.isInteger(n) && n > 0 ? n : null;
}
function isoOrNull(local) {
  return local ? new Date(local).toISOString() : null;
}
function validDropId(id) {
  return id.length > 0 && id.length <= 64;
}

// Paste box plus "load file" for wallet lists and snapshot CSVs.
function ListInput({ value, onChange, placeholder, rows = 8 }) {
  const onFile = async (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    try { onChange(await readFileText(f)); } catch (err) { onChange(""); }
    e.target.value = "";
  };
  return (
    <div style={{ display: "grid", gap: 6 }}>
      <textarea style={{ ...input, fontFamily: "monospace", minHeight: rows * 18 }} placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} />
      <label style={label}>
        Or load a file (.csv or .txt){" "}
        <input type="file" accept=".csv,.txt,text/csv,text/plain" onChange={onFile} />
      </label>
    </div>
  );
}

function ParseSummary({ count, noun, invalid, duplicates }) {
  return (
    <div style={{ ...label, fontSize: 13 }}>
      {fmtNum(count)} {noun}
      {duplicates > 0 && ` · ${fmtNum(duplicates)} repeated ${duplicates === 1 ? "line" : "lines"} ignored`}
      {invalid.length > 0 && (
        <span style={{ color: COLORS.warn }}>
          {` · ${fmtNum(invalid.length)} ${invalid.length === 1 ? "line is" : "lines are"} not valid and will not be sent (first: ${String(invalid[0]).slice(0, 60)})`}
        </span>
      )}
    </div>
  );
}

// ── (a) Single code ──────────────────────────────────────────────────────────
function SingleCodeCard({ flash, onCreated }) {
  const [points, setPoints] = useState("");
  const [code, setCode] = useState("");
  const [expires, setExpires] = useState("");
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    const p = positiveInt(points);
    if (!p) { flash("error", "Enter a positive whole number of points"); return; }
    setBusy(true);
    try {
      const body = { points: p };
      if (code.trim()) body.code = code.trim();
      if (expires) body.expires_at = isoOrNull(expires);
      const r = await createRewardsDropCode(body);
      setCreated(r);
      flash("success", `Created drop code ${r?.code}`);
      setPoints(""); setCode(""); setExpires("");
      onCreated();
    } catch (err) { flash("error", `Create failed: ${apiError(err, "unknown error")}`); }
    finally { setBusy(false); }
  };

  return (
    <div style={card}>
      <h5 style={{ marginTop: 0 }}>Single gift code</h5>
      <AppPlacement>Settings › Rewards › Redeem code.</AppPlacement>
      <form onSubmit={submit} style={grid}>
        <input style={input} type="number" min="1" step="1" placeholder="Points *" value={points} onChange={(e) => setPoints(e.target.value)} />
        <input style={input} placeholder="Code (optional, generated if blank)" value={code} onChange={(e) => setCode(e.target.value)} />
        <label style={label}>Expires (optional)
          <input style={input} type="datetime-local" value={expires} onChange={(e) => setExpires(e.target.value)} />
        </label>
        <button style={busyBtn(busy)} disabled={busy}>Create code</button>
      </form>
      {created && (
        <div style={resultBox}>
          Code <strong>{created.code}</strong> · {fmtNum(created.points)} points{created.expires_at ? ` · expires ${fmtDate(created.expires_at)}` : " · no expiry"}
        </div>
      )}
    </div>
  );
}

// ── (b) Bulk codes ───────────────────────────────────────────────────────────
function BulkCodesCard({ flash, onCreated }) {
  const [count, setCount] = useState("");
  const [points, setPoints] = useState("");
  const [expires, setExpires] = useState("");
  const [prefix, setPrefix] = useState("");
  const [dropId, setDropId] = useState("");
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [codes, setCodes] = useState(null);

  const csvName = () => `drop-codes-${(dropId.trim() || prefix.trim() || "bulk").replace(/[^A-Za-z0-9_-]/g, "_")}-${new Date().toISOString().slice(0, 10)}.csv`;
  const download = (list) =>
    downloadCsv(csvName(), toCsv(list, [
      { label: "code", value: "code" },
      { label: "points", value: "points" },
      { label: "expires_at", value: (r) => r.expires_at || "" },
      { label: "drop_id", value: (r) => r.drop_id || "" },
    ]));

  const submit = async (e) => {
    e.preventDefault();
    const n = positiveInt(count);
    const p = positiveInt(points);
    if (!n || n > 1000) { flash("error", "Count must be a whole number from 1 to 1000"); return; }
    if (!p) { flash("error", "Enter a positive whole number of points"); return; }
    if (dropId.trim() && !validDropId(dropId.trim())) { flash("error", "Drop id must be at most 64 characters"); return; }
    setBusy(true);
    try {
      const body = { count: n, points: p };
      if (expires) body.expires_at = isoOrNull(expires);
      if (prefix.trim()) body.prefix = prefix.trim();
      if (dropId.trim()) body.drop_id = dropId.trim();
      if (title.trim()) body.title = title.trim();
      const r = await createRewardsDropCodesBulk(body);
      const list = normaliseCodes(r?.codes, { points: p, expires_at: body.expires_at, drop_id: body.drop_id });
      setCodes(list);
      flash("success", `Created ${fmtNum(list.length)} codes. Download the CSV to hand them out.`);
      onCreated();
    } catch (err) { flash("error", `Bulk create failed: ${apiError(err, "unknown error")}`); }
    finally { setBusy(false); }
  };

  return (
    <div style={card}>
      <h5 style={{ marginTop: 0 }}>Bulk codes</h5>
      <AppPlacement>Settings › Rewards › Redeem code.</AppPlacement>
      <form onSubmit={submit} style={grid}>
        <div style={twoCol}>
          <input style={input} type="number" min="1" max="1000" step="1" placeholder="How many (1 to 1000) *" value={count} onChange={(e) => setCount(e.target.value)} />
          <input style={input} type="number" min="1" step="1" placeholder="Points per code *" value={points} onChange={(e) => setPoints(e.target.value)} />
          <input style={input} placeholder="Prefix (optional, e.g. WANK)" value={prefix} onChange={(e) => setPrefix(e.target.value)} />
          <label style={label}>Expires (optional)
            <input style={input} type="datetime-local" value={expires} onChange={(e) => setExpires(e.target.value)} />
          </label>
          <input style={input} placeholder="Drop id (optional, groups the codes)" value={dropId} onChange={(e) => setDropId(e.target.value)} />
          <input style={input} placeholder="Title (optional, shown in the app feed)" value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>
        <button style={busyBtn(busy)} disabled={busy}>{busy ? "Generating…" : "Generate codes"}</button>
      </form>
      {codes && (
        <div style={resultBox}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 8 }}>
            <span>{fmtNum(codes.length)} codes created. Each is single use.</span>
            <button style={btn} onClick={() => download(codes)} disabled={!codes.length}>Download CSV</button>
          </div>
          <div style={{ fontFamily: "monospace", fontSize: 12, maxHeight: 160, overflowY: "auto" }}>
            {codes.slice(0, 20).map((c) => <div key={c.code}>{c.code}</div>)}
            {codes.length > 20 && <Muted>and {fmtNum(codes.length - 20)} more in the CSV</Muted>}
          </div>
        </div>
      )}
    </div>
  );
}

// ── (c) Codes list ───────────────────────────────────────────────────────────
function CodesListCard({ flash, refreshKey }) {
  const [rows, setRows] = useState(null);
  const [meta, setMeta] = useState({ page: 1, total_pages: 1, total: 0 });
  const [page, setPage] = useState(1);
  const [redeemed, setRedeemed] = useState("");

  const load = useCallback(async () => {
    setRows(null);
    try {
      const res = await getRewardsDropCodes({ page, page_size: 25, redeemed: redeemed || undefined });
      setRows(listOf(res, ["codes", "drop_codes", "rows", "items"]));
      setMeta(pageMetaOf(res));
    } catch (err) {
      flash("error", apiError(err, "Failed to load drop codes"));
      setRows([]);
    }
  }, [flash, page, redeemed, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  return (
    <div style={card}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
        <h5 style={{ margin: 0 }}>Codes</h5>
        <div style={{ display: "flex", gap: 8 }}>
          <select style={{ ...input, width: 180 }} value={redeemed} onChange={(e) => { setPage(1); setRedeemed(e.target.value); }} aria-label="Filter by redeemed">
            <option value="">All codes</option>
            <option value="false">Not redeemed</option>
            <option value="true">Redeemed</option>
          </select>
          <button style={btnGhost} onClick={load}>Refresh</button>
        </div>
      </div>
      {rows === null ? <Spinner /> : rows.length === 0 ? <Muted>No codes found.</Muted> : (
        <Table head={["Code", "Points", "Drop", "Expires", "Redeemed by", "Redeemed at"]}>
          {rows.map((c) => (
            <tr key={c.code}>
              <td style={{ ...td, fontFamily: "monospace" }}>{c.code}</td>
              <td style={td}>{fmtNum(c.points)}</td>
              <td style={td}>{c.drop_id || "-"}</td>
              <td style={td}>{c.expires_at ? fmtDate(c.expires_at) : "No expiry"}</td>
              <td style={td} title={c.redeemed_by || ""}>{c.redeemed_by ? shortAddr(c.redeemed_by) : "-"}</td>
              <td style={td}>{c.redeemed_at ? fmtDate(c.redeemed_at) : "-"}</td>
            </tr>
          ))}
        </Table>
      )}
      <Pagination page={meta.page} totalPages={meta.total_pages} total={meta.total} onPage={setPage} />
    </div>
  );
}

// ── (d) Whitelist drop ───────────────────────────────────────────────────────
function WhitelistDropCard({ flash, onCreated }) {
  const [dropId, setDropId] = useState("");
  const [title, setTitle] = useState("");
  const [points, setPoints] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const parsed = useMemo(() => parseWalletList(text), [text]);

  const submit = async (e) => {
    e.preventDefault();
    const p = positiveInt(points);
    if (!validDropId(dropId.trim())) { flash("error", "Enter a drop id (at most 64 characters)"); return; }
    if (!title.trim()) { flash("error", "Enter a title"); return; }
    if (!p) { flash("error", "Enter a positive whole number of points"); return; }
    if (!parsed.wallets.length) { flash("error", "Add at least one valid wallet address"); return; }
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Add ${parsed.wallets.length} wallets to drop ${dropId.trim()} at ${fmtNum(p)} points each?`)) return;
    setBusy(true);
    setResult(null);
    try {
      const r = await createRewardsWhitelistDrop({ drop_id: dropId.trim(), title: title.trim(), points: p, wallets: parsed.wallets });
      setResult(r);
      flash("success", `Whitelist saved: ${fmtNum(r?.added)} added, ${fmtNum(r?.skipped)} skipped.`);
      onCreated();
    } catch (err) { flash("error", `Whitelist drop failed: ${apiError(err, "unknown error")}`); }
    finally { setBusy(false); }
  };

  return (
    <div style={card}>
      <h5 style={{ marginTop: 0 }}>Whitelist drop</h5>
      <AppPlacement warn>
        the current app has no drops list and no claim button, so listed wallets cannot claim this drop until the
        Rewards update ships. Use a gift code or a snapshot drop to reach users today.
      </AppPlacement>
      <p style={{ ...label, marginTop: 0 }}>
        Listed wallets can claim the drop in the app, once each. Posting again with the same drop id adds wallets and keeps the existing ones.
      </p>
      <form onSubmit={submit} style={grid}>
        <div style={twoCol}>
          <input style={input} placeholder="Drop id * (e.g. WEEK-42)" value={dropId} onChange={(e) => setDropId(e.target.value)} />
          <input style={input} type="number" min="1" step="1" placeholder="Points per wallet *" value={points} onChange={(e) => setPoints(e.target.value)} />
        </div>
        <input style={input} placeholder="Title * (shown in the app)" value={title} onChange={(e) => setTitle(e.target.value)} />
        <ListInput value={text} onChange={setText} placeholder={"One wallet per line, or a CSV with the wallet in the first column\n0x1234...\n0xabcd..."} />
        {text.trim() && <ParseSummary count={parsed.wallets.length} noun="wallets" invalid={parsed.invalid} duplicates={parsed.duplicates} />}
        <button style={busyBtn(busy)} disabled={busy}>{busy ? "Saving…" : "Create whitelist drop"}</button>
      </form>
      {result && (
        <div style={resultBox}>
          Drop <strong>{dropId.trim()}</strong>: {fmtNum(result.added)} wallets added, {fmtNum(result.skipped)} skipped (already listed or not valid).
        </div>
      )}
    </div>
  );
}

// ── (e) Snapshot drop ────────────────────────────────────────────────────────
function SnapshotDropCard({ flash, onCreated }) {
  const [dropId, setDropId] = useState("");
  const [title, setTitle] = useState("");
  const [total, setTotal] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const parsed = useMemo(() => parseBalances(text), [text]);
  const split = useMemo(() => splitSnapshot(total, parsed.balances), [total, parsed]);

  const submit = async (e) => {
    e.preventDefault();
    const t = positiveInt(total);
    if (!validDropId(dropId.trim())) { flash("error", "Enter a drop id (at most 64 characters)"); return; }
    if (!title.trim()) { flash("error", "Enter a title"); return; }
    if (!t) { flash("error", "Enter the total points as a positive whole number"); return; }
    if (!parsed.balances.length) { flash("error", "Add at least one valid wallet,balance line"); return; }
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Credit ${fmtNum(split.credited)} points across ${parsed.balances.length} wallets for drop ${dropId.trim()}? Credits are final.`)) return;
    setBusy(true);
    setResult(null);
    try {
      const r = await createRewardsSnapshotDrop({ drop_id: dropId.trim(), title: title.trim(), total_points: t, balances: parsed.balances });
      setResult(r);
      flash("success", `Snapshot credited: ${fmtNum(r?.credited)} wallets, ${fmtNum(r?.skipped)} skipped, ${fmtNum(r?.duplicates)} already credited.`);
      onCreated();
    } catch (err) { flash("error", `Snapshot drop failed: ${apiError(err, "unknown error")}`); }
    finally { setBusy(false); }
  };

  const zeroShares = split.rows.filter((r) => r.points === 0).length;

  return (
    <div style={card}>
      <h5 style={{ marginTop: 0 }}>Snapshot drop</h5>
      <AppPlacement>points land in the Rewards balance and a push opens Notifications (gift row). There is no rewards history screen yet.</AppPlacement>
      <p style={{ ...label, marginTop: 0 }}>
        Splits a pool of points across wallets in proportion to their balance and credits each wallet once. Re-submitting the same drop id does not credit a wallet twice.
      </p>
      <form onSubmit={submit} style={grid}>
        <div style={twoCol}>
          <input style={input} placeholder="Drop id * (e.g. PARTNER-OCT)" value={dropId} onChange={(e) => setDropId(e.target.value)} />
          <input style={input} type="number" min="1" step="1" placeholder="Total points to split *" value={total} onChange={(e) => setTotal(e.target.value)} />
        </div>
        <input style={input} placeholder="Title * (shown in the app feed)" value={title} onChange={(e) => setTitle(e.target.value)} />
        <ListInput value={text} onChange={setText} placeholder={"wallet,balance per line (header optional)\n0x1234...,1500\n0xabcd...,250"} />
        {text.trim() && <ParseSummary count={parsed.balances.length} noun="wallets" invalid={parsed.invalid} duplicates={parsed.duplicates} />}
        <button style={busyBtn(busy)} disabled={busy}>{busy ? "Crediting…" : "Credit snapshot drop"}</button>
      </form>

      {split.rows.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div style={{ marginBottom: 8 }}>
            Preview: {fmtNum(split.credited)} points to {fmtNum(split.rows.length)} wallets (balance total {fmtNum(split.sum)}).
            {split.credited !== Number(total) && <Muted> Rounding moves the total by {fmtNum(split.credited - Number(total))}.</Muted>}
            {zeroShares > 0 && <span style={{ color: COLORS.warn }}> {fmtNum(zeroShares)} wallets round to 0 points.</span>}
          </div>
          <div style={{ maxHeight: 320, overflowY: "auto" }}>
            <Table head={["Wallet", "Balance", "Share", "Points"]}>
              {split.rows.slice(0, 200).map((r) => (
                <tr key={r.wallet}>
                  <td style={{ ...td, fontFamily: "monospace" }} title={r.wallet}>{shortAddr(r.wallet)}</td>
                  <td style={td}>{fmtNum(r.balance)}</td>
                  <td style={td}>{(r.share * 100).toFixed(3)}%</td>
                  <td style={td}>{fmtNum(r.points)}</td>
                </tr>
              ))}
            </Table>
          </div>
          {split.rows.length > 200 && <Muted>Showing the first 200 of {fmtNum(split.rows.length)} wallets.</Muted>}
        </div>
      )}

      {result && (
        <div style={resultBox}>
          Drop <strong>{dropId.trim()}</strong>: {fmtNum(result.credited)} credited, {fmtNum(result.skipped)} skipped, {fmtNum(result.duplicates)} already credited before.
        </div>
      )}
    </div>
  );
}

// ── (f) Drops list ───────────────────────────────────────────────────────────
function DropsListCard({ flash, refreshKey }) {
  const [rows, setRows] = useState(null);
  const load = useCallback(async () => {
    setRows(null);
    try { setRows(listOf(await getRewardsDrops(), ["drops", "rows", "items"])); }
    catch (err) { flash("error", apiError(err, "Failed to load drops")); setRows([]); }
  }, [flash, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const num = (v) => (v === null || v === undefined ? "-" : fmtNum(v));
  return (
    <div style={card}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
        <h5 style={{ margin: 0 }}>Drops</h5>
        <button style={btnGhost} onClick={load}>Refresh</button>
      </div>
      {rows === null ? <Spinner /> : rows.length === 0 ? <Muted>No drops yet.</Muted> : (
        <Table head={["Drop", "Kind", "Title", "Points", "Wallets / codes", "Claimed / credited", "Created"]}>
          {rows.map((d) => (
            <tr key={d.drop_id}>
              <td style={{ ...td, fontFamily: "monospace" }}>{d.drop_id}</td>
              <td style={td}><Badge tone="success">{d.kind}</Badge></td>
              <td style={td}>{d.title || "-"}</td>
              <td style={td} title={d.kind === "snapshot" ? "Total pool" : "Per wallet"}>{fmtNum(d.points)}</td>
              <td style={td}>{num(d.wallets ?? d.codes ?? d.credited_wallets)}</td>
              <td style={td}>{num(d.kind === "snapshot" ? d.credited_wallets : (d.claimed ?? d.codes_redeemed))}</td>
              <td style={td}>{fmtDate(d.created_at)}</td>
            </tr>
          ))}
        </Table>
      )}
    </div>
  );
}

export default function DropsTab({ flash, canDrops }) {
  const [refreshKey, setRefreshKey] = useState(0);
  const bump = useCallback(() => setRefreshKey((k) => k + 1), []);
  return (
    <div>
      {canDrops ? (
        <>
          <SingleCodeCard flash={flash} onCreated={bump} />
          <BulkCodesCard flash={flash} onCreated={bump} />
          <WhitelistDropCard flash={flash} onCreated={bump} />
          <SnapshotDropCard flash={flash} onCreated={bump} />
          <CodesListCard flash={flash} refreshKey={refreshKey} />
        </>
      ) : (
        <div style={card}><ReadOnlyNote>Read only. Creating drops and viewing codes needs the rewards.drops permission.</ReadOnlyNote></div>
      )}
      <DropsListCard flash={flash} refreshKey={refreshKey} />
    </div>
  );
}
